import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Identity } from 'spacetimedb';
import { SpacetimeDBProvider } from 'spacetimedb/react';
import { DbConnection, type ErrorContext } from '../module_bindings';
import { ConnectionStatusScreen } from './ConnectionStatusScreen';
import { DB_NAME, HOST } from './config';
import {
  findActive,
  loadStore,
  migrateLegacyIfNeeded,
} from './identityStore';
import { IdentityPicker } from './IdentityPicker';
import { Welcome } from './Welcome';
import { WelcomeBack } from './WelcomeBack';
import {
  setActiveTokenHash,
  setSessionState,
  useActiveTokenHash,
  useSessionState,
} from './hooks';
import { ConnectedShell } from './ConnectedShell';

/**
 * SessionGuard owns the SpacetimeDB connection lifecycle.
 *
 * The session state drives what's rendered, derived synchronously (no
 * intermediate `phase` local state). This avoids races where a state
 * change from the in-game menu / picker would briefly fall through to a
 * stale provider mount before the effect catches up.
 *
 * Boot rules (run once on mount):
 *
 * - 0 saved identities             → WELCOME (intentional entry, with a
 *                                    "Select your character" link to the
 *                                    empty picker for file-import flows)
 * - 1+ identities, active set      → WELCOME_BACK (explicit Enter / Log out)
 * - 1+ identities, no active       → IDENTITY_SELECT (the picker)
 *
 * After boot, transitions happen via setSessionState from the screens,
 * the in-game menu, or the ConnectedShell FSM driver.
 */
export function SessionGuard({ children }: { children: ReactNode }) {
  const sessionState = useSessionState();
  const activeTokenHash = useActiveTokenHash();
  const [booted, setBooted] = useState(false);

  // Reconnect-on-resume plumbing. The SDK's ConnectionManager ref-counts
  // connections and cancels its own cleanup if a remount happens in the
  // same commit, so a plain key-bump won't force a new socket. We tear
  // down the provider entirely for one tick (`reconnecting=true`), then
  // remount with an incremented epoch — the gap is enough for the
  // SDK's deferred cleanup to actually fire.
  const [reconnectEpoch, setReconnectEpoch] = useState(0);
  const [reconnecting, setReconnecting] = useState(false);

  const requestReconnect = useCallback(() => {
    setReconnecting(true);
  }, []);

  useEffect(() => {
    if (!reconnecting) return;
    const t = setTimeout(() => {
      setReconnectEpoch(e => e + 1);
      setReconnecting(false);
    }, 50);
    return () => clearTimeout(t);
  }, [reconnecting]);

  // Boot pass: read store, decide initial state.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await migrateLegacyIfNeeded(HOST, DB_NAME);
      if (cancelled) return;

      const store = loadStore(HOST, DB_NAME);

      if (store.identities.length === 0) {
        // First-time visitor → Welcome screen (intentional Enter click,
        // per the rule that landing → /play never silently spawns a
        // character).
        setActiveTokenHash('');
        setSessionState('WELCOME');
      } else if (store.activeTokenHash) {
        // Logged in → friendly Welcome Back rather than auto-login.
        setActiveTokenHash(store.activeTokenHash);
        setSessionState('WELCOME_BACK');
      } else {
        // Logged out (cleared active) → picker.
        setActiveTokenHash('');
        setSessionState('IDENTITY_SELECT');
      }

      setBooted(true);
    })();
    return () => {
      cancelled = true;
    };
    // Empty deps: this is the boot pass only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!booted) return <BootScreen />;

  // While reconnecting, render NO provider at all. This is the load-bearing
  // tick — without an unmounted gap, the SDK's deferred cleanup gets
  // cancelled by the immediate remount and we keep the dead socket.
  if (reconnecting) return <ConnectionStatusScreen label="Reconnecting…" />;

  // Render decision is a pure function of sessionState. No useEffect lag,
  // no chance of a wrong-keyed ConnectingShell remount during a transition.
  if (sessionState === 'WELCOME') return <Welcome />;
  if (sessionState === 'IDENTITY_SELECT') return <IdentityPicker />;
  if (sessionState === 'WELCOME_BACK') return <WelcomeBack />;

  // Everything else (CONNECTING / CONNECTED_NO_USER / PLAYING / BANNED)
  // happens inside a live SpacetimeDBProvider.
  return (
    <ConnectingShell
      key={`${activeTokenHash || 'fresh-anon'}-${reconnectEpoch}`}
      requestReconnect={requestReconnect}
    >
      {children}
    </ConnectingShell>
  );
}

function BootScreen() {
  return (
    <div
      style={{
        height: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0a0e18',
        color: '#e7d9b6',
        fontFamily: 'system-ui, sans-serif',
        opacity: 0.6,
      }}
    >
      Waking the forest…
    </div>
  );
}

function ConnectingShell({
  children,
  requestReconnect,
}: {
  children: ReactNode;
  requestReconnect: () => void;
}) {
  // Snapshot the token at mount; the parent keys this component on
  // activeTokenHash so a different identity remounts cleanly.
  const initialToken = useMemo(() => {
    const store = loadStore(HOST, DB_NAME);
    const active = findActive(store);
    return active?.token;
  }, []);

  // Capture the live connection's token in state — but DO NOT persist
  // the identity to localStorage yet. We only persist once the user
  // actually enters the world (i.e. transitions to PLAYING in
  // ConnectedShell). Otherwise we'd save half-identities for users
  // who close the tab between connect and clicking Enter.
  const [connectedToken, setConnectedToken] = useState<string | undefined>(
    initialToken
  );

  const builder = useMemo(
    () =>
      DbConnection.builder()
        .withUri(HOST)
        .withDatabaseName(DB_NAME)
        .withToken(initialToken)
        // Request `gzip` instead of `none` to save bandwidth on
        // subscription updates. This depends on the local SDK patch
        // at patches/spacetimedb@2.2.0.patch which rewrites the
        // SDK's `decompress()` to use `getReader().read()` instead
        // of `for await (... of readableStream)`. iOS WebKit on
        // iOS 18.x doesn't implement Symbol.asyncIterator on
        // ReadableStream (verified on a real iOS 18.7 device whose
        // Safari reports "Version/26.4"), so the SDK's stock for-
        // await crashed every decompression. Patched version works
        // on the same device end-to-end.
        //
        // If the patch ever fails or gets dropped on an SDK bump,
        // flip this back to 'none' as a guaranteed-safe fallback.
        .withCompression('gzip')
        .onConnect((_conn, _identity: Identity, token: string) => {
          // Just remember the token — don't write to localStorage.
          // Persistence happens in ConnectedShell on PLAYING transition.
          setConnectedToken(token);
        })
        .onDisconnect(() => {
          // Disconnect happens during identity switch, log-out, or
          // network failure. Session state transitions are owned by the
          // initiator; don't touch them here.
        })
        .onConnectError((_ctx: ErrorContext, err: Error) => {
          console.error('Connect error', err);
        }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialToken]
  );

  return (
    <SpacetimeDBProvider connectionBuilder={builder}>
      <ConnectedShell
        connectedToken={connectedToken}
        requestReconnect={requestReconnect}
      >
        {children}
      </ConnectedShell>
    </SpacetimeDBProvider>
  );
}
